'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useWindowInfo } from '@faceless-ui/window-info';
import styles from './styles.module.scss';
import { Button, Icon, RenderBlocks } from '../../components';
import colors from '../../styles/colors/index.module.scss';
import { constructClassName } from '../../utils/constructClassName';
import type { ExtractLayoutType, SharedBlockType } from '../types';

import type { PagesQuery } from '../../graphql/generated/schema';
import { BlockSettings_Margin_MarginBottom, BlockSettings_Padding_PaddingLeft, BlockSettings_Padding_PaddingRight } from '../../graphql/generated/schema';
import { BlockContainer } from '../../components/layouts';
import { SliderWithBlocks } from '../SliderWithBlocks';

export const VerticalSlider: React.FC<SharedBlockType & ExtractLayoutType<PagesQuery, 'VerticalSlider'>> = ({
  slides,
  autoScrollEnabled,
  autoScrollSpeed,
  blockSettings,
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(1);

  const containerInnerRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);

  const { width } = useWindowInfo();

  const animateScroll = (direction: 'up' | 'down', slideIndex: number) => {
    let selectedSlideIndex = slideIndex;
    let selectedDirection = direction;

    if (direction === 'up' && slideIndex >= 2) {
      setCurrentSlideIndex(slideIndex - 1);
    } else if (slideIndex + 1 <= slides?.length - 2) {
      setCurrentSlideIndex(slideIndex + 1);
    } else {
      setCurrentSlideIndex(1);
      selectedSlideIndex = 2;
      selectedDirection = 'up';
    }

    const firstSlideSize = containerInnerRef.current?.children[0]?.clientHeight || 0;
    const previousSlideSizes = Array.from({ length: selectedSlideIndex + (selectedDirection === 'down' ? 1 : -1) }, (_, i) => containerInnerRef.current?.children[i]?.clientHeight || 0).reduce((acc, curr) => acc + curr, 0) - firstSlideSize;
    const transformValue = `translateY(-${previousSlideSizes + (selectedSlideIndex * 20)}px)`;

    containerInnerRef.current!.style.transform = transformValue;
  };

  const animateScrollRef = useRef(animateScroll);

  useEffect(() => {
    if (autoScrollEnabled) {
      const interval = setInterval(() => {
        animateScrollRef.current('down', currentSlideIndex);
      }, autoScrollSpeed || 3000);

      return () => {
        clearInterval(interval);
      };
    }

    return () => { };
  }, [autoScrollEnabled, autoScrollSpeed, animateScrollRef, currentSlideIndex]);


  if ((width || 0) <= 768) {
    return (
      <div className={styles.sliderContainer}>
        <SliderWithBlocks
          sliderWithBlocks={slides}
          slidesToShow={{
            desktop: 1,
            tablet: 1,
            mobile: 1,
          }}
        />
      </div>
    );
  }

  return (
    <BlockContainer
      blockSettings={blockSettings}
      className={constructClassName([
        styles.container,
      ])}
    >
      <div className={styles.buttonContainer}>
        <Button
          title="Scroll up"
          onClick={() => animateScroll('up', currentSlideIndex)}
          type="tertiary"
          icon="circleChevronUpClassicThin"
          iconSize={32}
          iconFill={colors.secondary}
          disabled={currentSlideIndex <= 1}
        />
        <Button
          title="Scroll down"
          onClick={() => animateScroll('down', currentSlideIndex)}
          type="tertiary"
          icon="circleChevronDownClassicThin"
          iconSize={32}
          iconFill={colors.secondary}
        />
      </div>
      <div
        ref={containerInnerRef}
        className={styles.containerInner}
      >
        {slides?.map((testimonial, index) => (
          <div
            ref={slideRef}
            key={testimonial.id}
            className={constructClassName([
              styles.slide,
              currentSlideIndex === index && styles.activeSlide,
            ])}
          >
            {
              currentSlideIndex === index && (
                <Icon
                  className={styles.speechIcon}
                  icon="quoteLeftClassicSolid"
                  fill={colors.secondary}
                  size={52}
                />
              )
            }
            <RenderBlocks
              blockSettings={{
                margin: {
                  // eslint-disable-next-line camelcase
                  marginBottom: BlockSettings_Margin_MarginBottom.None,
                },
                padding: {
                  // eslint-disable-next-line camelcase
                  paddingLeft: BlockSettings_Padding_PaddingLeft.None,
                  // eslint-disable-next-line camelcase
                  paddingRight: BlockSettings_Padding_PaddingRight.None,
                },
              }}
              blocks={testimonial.blocks || []}
            />
          </div>
        ))}
      </div>
    </BlockContainer>
  );
};
